import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, Mail, Sparkles, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale, intlLocale } from '@/lib/i18n';
import {
  formatMinor,
  monthlyPriceMinor,
  usePublicPlanCatalog,
  type BillingInterval,
} from '@/lib/plan-catalog';
import { rich } from './legal-pages';

/* ---------------------------------------------------------------------------
   המחירים בדף המכירה. כל המספרים מהקטלוג (usePublicPlanCatalog — השרת, או
   העתק מקומי כשהוא לא זמין); שמות התוכניות והטקסט מ-public.json תחת
   landing.pricing. התוכנית המודגשת היא תוכנית הניסיון שבקטלוג.
   --------------------------------------------------------------------------- */

export function PricingSection() {
  const { t } = useTranslation('public');
  const catalog = usePublicPlanCatalog();
  const [interval, setBillingInterval] = useState<BillingInterval>('MONTHLY');
  const locale = currentLocale();
  const money = (minor: number) => formatMinor(minor, locale);
  const num = new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 2 });
  const trial = catalog.plans.find((p) => p.key === catalog.trialPlan);
  const days = catalog.trialDays;

  return (
    <section
      id="pricing"
      aria-labelledby="pricing-title"
      className="scroll-mt-20 border-y border-border bg-surface"
    >
      <div className="mx-auto max-w-6xl space-y-10 px-5 py-20">
        <header className="mx-auto max-w-2xl space-y-3 text-center">
          <p className="text-xs font-semibold ltr:uppercase ltr:tracking-widest text-accent">
            {t('landing.pricing.kicker')}
          </p>
          <h2 id="pricing-title" className="text-3xl font-bold tracking-tight text-fg sm:text-4xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="text-lg leading-relaxed text-fg-muted">
            {t('landing.pricing.subtitle', { days })}
          </p>
        </header>

        {/* חודשי / שנתי */}
        <div className="flex justify-center">
          <div
            role="group"
            aria-label={t('landing.pricing.intervalAria')}
            className="inline-flex rounded-full border border-border bg-surface-raised p-1 shadow-xs"
          >
            {(['MONTHLY', 'ANNUAL'] as const).map((iv) => (
              <button
                key={iv}
                type="button"
                aria-pressed={interval === iv}
                onClick={() => setBillingInterval(iv)}
                className={`inline-flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors duration-(--duration-fast) ${
                  interval === iv
                    ? 'bg-accent text-fg-on-accent shadow-sm'
                    : 'text-fg-muted hover:text-fg'
                }`}
              >
                {t(iv === 'MONTHLY' ? 'landing.pricing.monthly' : 'landing.pricing.annual')}
                {iv === 'ANNUAL' ? (
                  <span
                    className={`rounded-full px-2 py-0.5 text-2xs font-semibold ${
                      interval === iv
                        ? 'bg-white/20 text-fg-on-accent'
                        : 'bg-success-subtle text-success ring-1 ring-inset ring-success-border/60'
                    }`}
                  >
                    {t('landing.pricing.annualBadge')}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {catalog.plans.map((p) => {
            const featured = p.key === catalog.trialPlan;
            const price = monthlyPriceMinor(catalog, p.key, interval);
            const extraMax = p.seats + p.maxExtraSeats;
            return (
              <li
                key={p.key}
                className={`relative flex flex-col gap-5 rounded-(--radius-xl) border bg-surface-raised p-6 shadow-sm ${
                  featured
                    ? 'border-accent shadow-lg shadow-accent/15 ring-1 ring-accent'
                    : 'border-border'
                }`}
              >
                {featured ? (
                  <span className="absolute -top-3 start-6 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-0.5 text-xs font-semibold text-fg-on-accent shadow-sm">
                    <Sparkles className="size-3" aria-hidden />
                    {t('landing.pricing.recommended')}
                  </span>
                ) : null}
                <div className="space-y-1">
                  <h3 className="text-xl font-semibold text-fg">
                    {t(`landing.pricing.plans.${p.key}.name`)}
                  </h3>
                  <p className="text-sm text-fg-muted">{t(`landing.pricing.plans.${p.key}.for`)}</p>
                </div>
                <div className="space-y-1">
                  <p className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className="tabular text-4xl font-bold tracking-tight text-fg">
                      {money(price)}
                    </span>
                    <span className="text-sm text-fg-muted">{t('landing.pricing.perMonth')}</span>
                    <span className="text-sm font-medium text-fg-muted">
                      {catalog.vatRateBps > 0
                        ? t('landing.pricing.plusVat')
                        : t('landing.pricing.noVat')}
                    </span>
                  </p>
                  <p className="min-h-5 text-xs text-fg-subtle">
                    {interval === 'ANNUAL' ? (
                      <>
                        <s className="tabular me-1.5">{money(p.monthlyMinor)}</s>
                        {t(
                          catalog.vatRateBps > 0
                            ? 'landing.pricing.annualTotal'
                            : 'landing.pricing.annualTotalNoVat',
                          { total: money(p.annualMinor) },
                        )}
                      </>
                    ) : (
                      t('landing.pricing.monthlyBilling')
                    )}
                  </p>
                </div>
                <ul className="space-y-2.5 text-sm text-fg">
                  <li className="flex gap-2">
                    <Users className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
                    {t('landing.pricing.seats', { count: p.seats })}
                  </li>
                  <li className="flex gap-2">
                    <Sparkles className="mt-0.5 size-4 shrink-0 text-violet" aria-hidden />
                    {t('landing.pricing.aiBudget', { amount: money(p.aiBudgetMinor) })}
                  </li>
                  {p.extraSeatsAllowed && p.maxExtraSeats > 0 ? (
                    <li className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                      {t('landing.pricing.extraSeats', {
                        price: money(catalog.extraSeatMonthlyMinor),
                        max: Math.min(extraMax, catalog.maxTotalSeats),
                      })}
                    </li>
                  ) : null}
                </ul>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-col items-center gap-3 text-center">
          <Link
            to="/onboarding"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-(--radius-md) bg-accent px-6 text-base font-semibold text-fg-on-accent shadow-md shadow-accent/25 transition-[filter,background-color] duration-(--duration-fast) hover:bg-accent-hover"
          >
            {t('landing.pricing.cta', { days })}
            <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden />
          </Link>
          <p className="text-sm text-fg-muted">
            {trial
              ? t('landing.pricing.trialNote', {
                  plan: t(`landing.pricing.plans.${trial.key}.name`),
                  seats: trial.seats,
                })
              : null}{' '}
            {catalog.vatRateBps > 0
              ? t('landing.pricing.vatNote', { rate: num.format(catalog.vatRateBps / 100) })
              : t('landing.pricing.vatNoteExempt')}
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-3 rounded-(--radius-lg) border border-border bg-surface-raised p-5 lg:col-span-2">
            <h3 className="flex items-center gap-2 font-semibold text-fg">
              <Users className="size-4 text-accent" aria-hidden />
              {t('landing.pricing.seatsTitle')}
            </h3>
            <ul className="grid gap-2 text-sm text-fg-muted sm:grid-cols-2">
              <li className="flex gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                {t('landing.pricing.seatsAll')}
              </li>
              <li className="flex gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                {t('landing.pricing.seatsActive')}
              </li>
            </ul>
          </div>
          <div className="space-y-2 rounded-(--radius-lg) border border-border bg-surface-raised p-5">
            <h3 className="flex items-center gap-2 font-semibold text-fg">
              <Mail className="size-4 text-accent" aria-hidden />
              {t('landing.pricing.contactTitle', { max: catalog.maxTotalSeats })}
            </h3>
            <p className="text-sm text-fg-muted">{rich(t('landing.pricing.contactBody'))}</p>
            <p className="text-sm text-fg-muted">{rich(t('landing.pricing.founding'))}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
